import { format, parseISO } from 'date-fns';

import {
  DRAFT_STATUS,
  getDraftStatus,
  getDraftStatusChange,
  getNewerDraftVersion,
  isViewOnlyWhileBlocked
} from 'core/helpers/policy-drafting';
import { LAYMAN_DATE_FORMAT, laymanDateTimeFormatter } from 'core/helpers/formatters';
import icWorking from 'core/assets/svg/ic-draft-working.svg';
import icInReviewEdit from 'core/assets/svg/ic-draft-in-review-edit.svg';
import icInReviewCannotEdit from 'core/assets/svg/ic-draft-in-review-cannot-edit.svg';
import icChangeRequest from 'core/assets/svg/ic-draft-change-request.svg';
import icApproved from 'core/assets/svg/ic-draft-approved.svg';
import icDenied from 'core/assets/svg/ic-draft-denied.svg';
import icDeleted from 'core/assets/svg/ic-draft-deleted.svg';
import icWorkingCannotEdit from 'core/assets/svg/ic-draft-working-cannot-edit.svg';
import icOfficialPolicy from 'core/assets/svg/ic-draft-official-policy.svg';

export const FOOTER_STATUS = {
  ...DRAFT_STATUS,
  WORKING_VIEW_ONLY: 'workingViewOnly',
  OFFICIAL_POLICY: 'officialPolicy'
};

export const FOOTER_ACTION = {
  PREVIEW: 'preview',
  SEE_DRAFTS_TABLE: 'seeDraftsTable'
};

export const BOTTOM_BAR_TONE = {
  ORANGE: 'orange',
  BEIGE: 'beige'
};

const NOT_YET_KNOWN = '—';

const formatStatusChange = (verb, { by, at }) => {
  const when = at ? laymanDateTimeFormatter(at) : NOT_YET_KNOWN;
  return `${verb} by ${by ?? NOT_YET_KNOWN} on ${when}`;
};

const VIEWABLE_UNTIL_NEXT_CHANGE = 'but will be viewable until another change is sent for review or completed.';

const getStatusFooterContent = ({ status, canApproveUWReview, statusChange, effectiveDate, isCurrentSegment }) => {
  const formattedEffectiveDate = effectiveDate ? format(parseISO(effectiveDate), LAYMAN_DATE_FORMAT) : '';
  const draftHeading = { title: 'Endorsement draft', detail: `for ${formattedEffectiveDate}` };
  switch (status) {
    case FOOTER_STATUS.IN_REVIEW:
      return {
        ...draftHeading,
        icon: canApproveUWReview ? icInReviewEdit : icInReviewCannotEdit,
        pill: { status: DRAFT_STATUS.IN_REVIEW, viewOnly: !canApproveUWReview },
        subline: canApproveUWReview
          ? 'This draft requires your review.'
          : "This draft is in review, so this policy can't be edited.",
        showViewNotes: true,
        primaryAction: canApproveUWReview ? FOOTER_ACTION.PREVIEW : FOOTER_ACTION.SEE_DRAFTS_TABLE,
        bottomBar: {
          tone: BOTTOM_BAR_TONE.BEIGE,
          text: canApproveUWReview
            ? `${formatStatusChange('Sent', statusChange)}.`
            : `${formatStatusChange('Sent', statusChange)}—Reviews usually take a few hours, up to 24.`
        }
      };
    case FOOTER_STATUS.CHANGES_REQUESTED:
      return {
        ...draftHeading,
        icon: icChangeRequest,
        pill: { status: DRAFT_STATUS.CHANGES_REQUESTED },
        subline: 'This draft has requested changes. Update it and send it back for review.',
        showViewNotes: true,
        primaryAction: FOOTER_ACTION.PREVIEW,
        bottomBar: { tone: BOTTOM_BAR_TONE.BEIGE, text: `${formatStatusChange('Requested', statusChange)}.` }
      };
    case FOOTER_STATUS.APPROVED:
      return {
        ...draftHeading,
        icon: icApproved,
        pill: { status: DRAFT_STATUS.APPROVED },
        subline: 'This draft is approved. You may complete it to issue the endorsement.',
        showViewNotes: true,
        primaryAction: FOOTER_ACTION.PREVIEW,
        bottomBar: { tone: BOTTOM_BAR_TONE.BEIGE, text: `${formatStatusChange('Approved', statusChange)}.` }
      };
    case FOOTER_STATUS.DENIED:
      return {
        ...draftHeading,
        icon: icDenied,
        pill: { status: DRAFT_STATUS.DENIED },
        subline: canApproveUWReview
          ? 'This is a denied draft. It will never be issued.'
          : `This is a denied draft. It will never be issued, ${VIEWABLE_UNTIL_NEXT_CHANGE}`,
        showViewNotes: true,
        primaryAction: null,
        bottomBar: { tone: BOTTOM_BAR_TONE.BEIGE, text: `${formatStatusChange('Denied', statusChange)}.` }
      };
    case FOOTER_STATUS.DELETED:
      return {
        ...draftHeading,
        icon: icDeleted,
        pill: { status: DRAFT_STATUS.DELETED },
        subline: `This is a deleted draft. It will never be issued, ${VIEWABLE_UNTIL_NEXT_CHANGE}`,
        showViewNotes: true,
        primaryAction: null,
        bottomBar: { tone: BOTTOM_BAR_TONE.BEIGE, text: `${formatStatusChange('Deleted', statusChange)}.` }
      };
    case FOOTER_STATUS.OFFICIAL_POLICY:
      return {
        title: 'Official policy',
        detail: `viewing ${formattedEffectiveDate}, endorsement (${isCurrentSegment ? 'current' : 'past'})`,
        icon: icOfficialPolicy,
        pill: { viewOnly: true },
        subline:
          'When there’s a draft in the review process, you can’t make changes to the official policy, but you may view it.',
        showViewNotes: false,
        primaryAction: FOOTER_ACTION.SEE_DRAFTS_TABLE,
        bottomBar: null
      };
    case FOOTER_STATUS.WORKING_VIEW_ONLY:
      return {
        ...draftHeading,
        icon: icWorkingCannotEdit,
        pill: { status: DRAFT_STATUS.WORKING, viewOnly: true },
        subline:
          'When there’s a draft in the review process, you can’t make changes to other working drafts but you may view them.',
        showViewNotes: false,
        primaryAction: FOOTER_ACTION.SEE_DRAFTS_TABLE,
        bottomBar: { tone: BOTTOM_BAR_TONE.ORANGE }
      };
    default:
      return {
        ...draftHeading,
        icon: icWorking,
        pill: { status: DRAFT_STATUS.WORKING },
        subline:
          "This draft is a copy of the policy. It won't be saved onto the official one until its changes are either sent for review or completed.",
        showViewNotes: false,
        primaryAction: FOOTER_ACTION.PREVIEW,
        bottomBar: { tone: BOTTOM_BAR_TONE.ORANGE }
      };
  }
};

/** An older version of a draft keeps its status's footer, read-only: the drafts table leads to the latest version. */
const asOlderVersion = (content) => ({
  ...content,
  pill: { ...content.pill, viewOnly: true },
  subline:
    'This is an older version of this draft. Open the latest version from the drafts table to keep working on it.',
  primaryAction: FOOTER_ACTION.SEE_DRAFTS_TABLE
});

export const getDraftFooterContent = ({ isOlderVersion, ...state }) => {
  const content = getStatusFooterContent(state);
  return isOlderVersion ? asOlderVersion(content) : content;
};

/**
 * With no preview open, the caller only renders this footer while a draft on the policy is blocking, and it describes
 * the official policy at the segment selected in the sidebar.
 */
export const getFooterState = ({ preview, previewDrafts, segment, segments = [] }) => {
  if (!preview?.id) {
    return {
      status: FOOTER_STATUS.OFFICIAL_POLICY,
      effectiveDate: segment?.startDate,
      isCurrentSegment: segment?.segmentId === segments.at(-1)?.segmentId
    };
  }
  const effectiveDate = preview.changeDetails?.effectiveDate;
  if (isViewOnlyWhileBlocked({ preview, previewDrafts })) {
    return { status: FOOTER_STATUS.WORKING_VIEW_ONLY, effectiveDate };
  }
  return {
    status: getDraftStatus(preview),
    effectiveDate,
    statusChange: getDraftStatusChange(preview),
    isOlderVersion: Boolean(getNewerDraftVersion({ preview, previewDrafts }))
  };
};
